import {
  ReferenceArrayInput,
  Button, CreateButton, DeleteButton, EditButton, SaveButton, SimpleForm, TextField, Toolbar, TopToolbar,
  useTranslate,
} from 'react-admin';
import { Datagrid } from '@react-admin/ra-rbac';
import { ListLive } from '@react-admin/ra-realtime';
import {
  required, useRecordContext,
} from 'ra-core';
import FireExtinguisherIcon from '@mui/icons-material/FireExtinguisher';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import Battery5BarIcon from '@mui/icons-material/Battery5Bar';
import BlindIcon from '@mui/icons-material/Blind';
import AssistWalkIcon from '@mui/icons-material/AssistWalker';
import ComputerIcon from '@mui/icons-material/Computer';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import SmartScreenIcon from '@mui/icons-material/SmartScreen';
import RouterIcon from '@mui/icons-material/Router';
import CribIcon from '@mui/icons-material/Crib';
import CoffeeMakerIcon from '@mui/icons-material/CoffeeMaker';
import DiningIcon from '@mui/icons-material/Dining';
import EmojiFlagsIcon from '@mui/icons-material/EmojiFlags';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EscalatorIcon from '@mui/icons-material/Escalator';
import FactoryIcon from '@mui/icons-material/Factory';
import RadioIcon from '@mui/icons-material/Radio';
import SecurityIcon from '@mui/icons-material/Security';
import CameraIndoorIcon from '@mui/icons-material/CameraIndoor';
import CameraOutdoorIcon from '@mui/icons-material/CameraOutdoor';
import CatchingPokemonIcon from '@mui/icons-material/CatchingPokemon';
import CleanHandsIcon from '@mui/icons-material/CleanHands';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import GarageIcon from '@mui/icons-material/Garage';
import HandymanIcon from '@mui/icons-material/Handyman';
import HardwareIcon from '@mui/icons-material/Hardware';
import HealingIcon from '@mui/icons-material/Healing';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import HeatPumpIcon from '@mui/icons-material/HeatPump';
import HubIcon from '@mui/icons-material/Hub';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { LIST_DEBOUNCE } from '../../../config';
import SearchFields from '../components/fields/Filters';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { GrayAutocompleteArrayInputWithUpperLabel } from '../components/inputs/GrayAutocompleteArrayInputWithUpperLabel';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { GrayTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayTimeGranularityInputWithUpperLabel';
import { IconSelector } from '../components/inputs/IconSelector';
import { IconField } from '../components/fields/IconField';

const iconMap = {
  'fire-extinguisher': FireExtinguisherIcon,
  'medical-services': MedicalServicesIcon,
  'monitor-heart': MonitorHeartIcon,
  battery: Battery5BarIcon,
  'blind-assistance': BlindIcon,
  'assist-walker': AssistWalkIcon,
  computer: ComputerIcon,
  headphones: HeadphonesIcon,
  'tablet-mac': TabletMacIcon,
  router: RouterIcon,
  crib: CribIcon,
  'coffee-maker': CoffeeMakerIcon,
  dining: DiningIcon,
  'emoji-flags': EmojiFlagsIcon,
  engineering: EngineeringIcon,
  escalator: EscalatorIcon,
  factory: FactoryIcon,
  radio: RadioIcon,
  security: SecurityIcon,
  'camera-indoor': CameraIndoorIcon,
  'camera-outdoor': CameraOutdoorIcon,
  'catching-pokemon': CatchingPokemonIcon,
  'clean-hands': CleanHandsIcon,
  'electric-bolt': ElectricBoltIcon,
  fireplace: FireplaceIcon,
  garage: GarageIcon,
  handyman: HandymanIcon,
  hardware: HardwareIcon,
  healing: HealingIcon,
  'health-and-safety': HealthAndSafetyIcon,
  'heart-broken': HeartBrokenIcon,
  'heat-pump': HeatPumpIcon,
  hub: HubIcon,
  'smart-screen': SmartScreenIcon,
};

export const InventoryCategoriesList = () => {

  const translate = useTranslate();

  return (
    <>
      <h3>{translate('resources.security_inventory_categories.title')}</h3>
      <p>{translate('resources.security_inventory_categories.text.sub_title')}</p>
      <ListLive filters={SearchFields} debounce={LIST_DEBOUNCE}
        actions={<TopToolbar><CreateButton label="general.text.add_from_presets" /></TopToolbar>}>
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <TextField source="name" label="general.fields.name"/>
          <IconField source="icon" iconMap={iconMap} />
          <TimeGranularityField source="expiryInterval" label="resources.security_inventory_categories.fields.inspection_period"/>
          <AssetsInDialogButton label="resources.security_inventory_categories.fields.people_to_notify" resource="security-inventory-categories" />
          <EditButton resource="security-inventory-categories"/>
          <DeleteButton resource="security-inventory-categories"/>
        </Datagrid>
      </ListLive>
    </>
  );

};

const inventoryCategoryEditTransformer = (data: any) => {

  return {
    ...data,
    peopleToNotify: (data.peopleToNotify ?? []).map((personId: string) => ({
      _id: personId,
      _ref: 'Asset',
    })),
  };

};

const InventoryCategoryEditToolbar = () => {

  const navigate = useNavigate();

  return (
    <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1000px' }}>
      <Button label='ra.action.back' onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
      <SaveButton />
    </Toolbar>
  );

};

const InventoryCategoryEditForm = () => {

  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>"{record?.name}" {translate('general.text.settings')}</h4>
      <Typography variant="body1">{translate('resources.security_inventory_categories.text.settings_sub_title')}</Typography>
      <hr/>
      <GrayTextInputWithUpperLabel source="name" validate={required()} label="general.fields.name"/>
      <IconSelector source="icon" iconMap={iconMap} label="resources.security-inventory-categories.fields.icon" />
      <ReferenceArrayInput source="peopleToNotify" reference="app-users" label="resources.security_inventory_categories.fields.people_to_notify">
        <GrayAutocompleteArrayInputWithUpperLabel label="resources.security_inventory_categories.fields.people_to_notify"/>
      </ReferenceArrayInput>
      <GrayTimeGranularityInputWithUpperLabel source="expiryInterval" label="resources.security_inventory_categories.fields.inspection_period"/>
    </>
  );

};

export const InventoryCategoryEdit = () => {

  return (
    <>
      <EditWithSuccess transform={inventoryCategoryEditTransformer} title="resources.security_inventory_categories.title" actions={false}>
        <SimpleForm sx={{ maxWidth: '1000px' }} toolbar={<InventoryCategoryEditToolbar/>}>
          <InventoryCategoryEditForm/>
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};
